import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Avatar, ErrorNotice, Spinner } from '@/components/ui';
import {
  useCreateUser,
  useDeleteUser,
  useInvites,
  useIssueInvite,
  useResetPassword,
  useRevokeInvite,
  useUpdateUser,
  useUsers,
} from '@/hooks/queries';
import type { CreatedUserResponse, GlobalRole, InviteStatus, IssuedInvite, User } from '@/types/api';
import { useState, type FormEvent } from 'react';

/**
 * Panel del administrador: alta manual de empleados. La contrasena temporal se
 * muestra una unica vez tras crear la cuenta o resetearla; despues solo existe
 * como hash, asi que no hay forma de volver a consultarla.
 */
export function AdminUsersPage() {
  const [search, setSearch] = useState('');
  const [credentials, setCredentials] = useState<CreatedUserResponse | null>(null);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);

  const { data, isLoading } = useUsers(search);
  const createUser = useCreateUser();
  const resetPassword = useResetPassword();
  const updateUser = useUpdateUser();
  const deleteUser = useDeleteUser();

  function handleCreate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const values = new FormData(form);

    createUser.mutate(
      {
        email: String(values.get('email')),
        fullName: String(values.get('fullName')),
        jobTitle: String(values.get('jobTitle') || '') || undefined,
        role: values.get('role') === 'ADMIN' ? 'ADMIN' : 'MEMBER',
      },
      {
        onSuccess: (result) => {
          setCredentials(result);
          form.reset();
        },
      },
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-auto px-6 py-5">
      {credentials && (
        <TemporaryPasswordNotice data={credentials} onDismiss={() => setCredentials(null)} />
      )}

      <InvitesSection />

      <section className="rounded-panel border border-line bg-surface">
        <header className="border-b border-line-soft bg-sunken px-4 py-[13px]">
          <h2 className="font-display text-sm font-semibold">Crear cuenta a mano</h2>
          <p className="mt-0.5 text-[12.5px] text-subtle text-pretty">
            Alternativa al token de registro: se genera una clave temporal que la persona debera
            cambiar al entrar.
          </p>
        </header>

        <form onSubmit={handleCreate} className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-4">
          <Field label="Correo" name="email" type="email" required />
          <Field label="Nombre completo" name="fullName" required />
          <Field label="Puesto" name="jobTitle" placeholder="Opcional" />
          <div>
            <label htmlFor="role" className="mb-1.5 block text-[12.5px] font-semibold">
              Rol
            </label>
            <select
              id="role"
              name="role"
              defaultValue="MEMBER"
              className="w-full rounded-control border border-line-strong bg-surface px-3 py-2 text-[13.5px] outline-none focus:border-brand"
            >
              <option value="MEMBER">Miembro</option>
              <option value="ADMIN">Administrador</option>
            </select>
          </div>

          {createUser.error && (
            <div className="sm:col-span-2 lg:col-span-4">
              <ErrorNotice>{(createUser.error as Error).message}</ErrorNotice>
            </div>
          )}

          <div className="sm:col-span-2 lg:col-span-4">
            <button
              type="submit"
              disabled={createUser.isPending}
              className="rounded-control bg-brand px-4 py-2 text-[13px] font-semibold text-brand-on transition-colors hover:bg-brand-strong disabled:opacity-50"
            >
              {createUser.isPending ? 'Creando…' : 'Crear usuario'}
            </button>
          </div>
        </form>
      </section>

      <section className="rounded-panel border border-line bg-surface">
        <header className="flex items-center gap-3 border-b border-line-soft bg-sunken px-4 py-[13px]">
          <h2 className="font-display text-sm font-semibold">Equipo</h2>
          <p className="text-[12.5px] text-subtle">
            {data?.total ?? 0} {data?.total === 1 ? 'persona' : 'personas'}
          </p>
          <div className="flex-1" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nombre o correo"
            aria-label="Buscar usuarios"
            className="w-56 rounded-control border border-line-strong bg-surface px-3 py-1.5 text-[13px] outline-none focus:border-brand"
          />
        </header>

        {isLoading ? (
          <div className="flex justify-center py-10">
            <Spinner />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-[13px]">
              <thead className="border-b border-line-soft text-[10px] uppercase tracking-[.1em] text-faint">
                <tr>
                  <th scope="col" className="px-4 py-2.5 font-semibold">
                    Persona
                  </th>
                  <th scope="col" className="px-4 py-2.5 font-semibold">
                    Correo
                  </th>
                  <th scope="col" className="px-4 py-2.5 font-semibold">
                    Rol
                  </th>
                  <th scope="col" className="px-4 py-2.5 font-semibold">
                    Estado
                  </th>
                  <th scope="col" className="px-4 py-2.5 text-right font-semibold">
                    <span className="sr-only">Acciones</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {data?.data.map((user) => (
                  <tr key={user.id} className="border-b border-line-soft last:border-b-0">
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2.5">
                        <Avatar user={user} size={28} />
                        <div className="min-w-0">
                          <p className="truncate font-semibold">{user.fullName}</p>
                          <input
                            defaultValue={user.jobTitle ?? ''}
                            placeholder="Sin puesto"
                            aria-label={`Puesto de ${user.fullName}`}
                            onBlur={(e) => {
                              const value = e.target.value.trim();
                              if (value !== (user.jobTitle ?? '')) {
                                updateUser.mutate({ userId: user.id, jobTitle: value || null });
                              }
                            }}
                            className="w-full rounded border border-transparent bg-transparent text-[11.5px] text-subtle outline-none transition-colors hover:border-line focus:border-brand focus:bg-surface"
                          />
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-subtle">{user.email}</td>
                    <td className="px-4 py-2.5">
                      <select
                        value={user.role}
                        aria-label={`Permiso de ${user.fullName}`}
                        onChange={(e) =>
                          updateUser.mutate({
                            userId: user.id,
                            role: e.target.value as GlobalRole,
                          })
                        }
                        className="rounded-control border border-line-strong bg-surface px-2 py-1 text-[12.5px] outline-none focus:border-brand"
                      >
                        <option value="MEMBER">Miembro</option>
                        <option value="ADMIN">Administrador</option>
                      </select>
                    </td>
                    <td className="px-4 py-2.5">
                      {user.mustChangePassword ? (
                        <span className="rounded-md border border-warn-line bg-warn-soft px-2 py-0.5 text-[11px] font-semibold text-warn-text">
                          Clave pendiente
                        </span>
                      ) : (
                        <span className="rounded-md border border-success-line bg-success-soft px-2 py-0.5 text-[11px] font-semibold text-success-text">
                          Activo
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() =>
                            resetPassword.mutate(user.id, {
                              onSuccess: (result) => setCredentials(result),
                            })
                          }
                          className="rounded-[7px] border border-line-strong px-2 py-1 text-[11.5px] font-medium transition-colors hover:border-line-hover"
                        >
                          Resetear clave
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            updateUser.mutate({
                              userId: user.id,
                              status: user.status === 'SUSPENDED' ? 'ACTIVE' : 'SUSPENDED',
                            })
                          }
                          className="rounded-[7px] border border-line-strong px-2 py-1 text-[11.5px] font-medium transition-colors hover:border-line-hover"
                        >
                          {user.status === 'SUSPENDED' ? 'Reactivar' : 'Suspender'}
                        </button>
                        <button
                          type="button"
                          onClick={() => setUserToDelete(user)}
                          className="rounded-[7px] border border-line-strong px-2 py-1 text-[11.5px] font-medium text-danger transition-colors hover:border-danger"
                        >
                          Eliminar
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {userToDelete && (
        <ConfirmDialog
          title={`Eliminar a ${userToDelete.fullName}`}
          description={
            <>
              La cuenta <strong>{userToDelete.email}</strong> dejara de tener acceso y su sesion se
              cerrara. Sus tareas y comentarios se conservan para no romper el historial del
              equipo.
            </>
          }
          requireTyping={userToDelete.username}
          pending={deleteUser.isPending}
          error={deleteUser.error ? (deleteUser.error as Error).message : null}
          onCancel={() => setUserToDelete(null)}
          onConfirm={() =>
            deleteUser.mutate(userToDelete.id, { onSuccess: () => setUserToDelete(null) })
          }
        />
      )}
    </div>
  );
}

/**
 * Invitaciones de registro.
 *
 * Es la via normal para sumar a alguien: se le manda el token, se registra con
 * su cuenta de Google y elige su propia contrasena. El token vale 24 horas y
 * una sola vez, de modo que nadie ajeno pueda crearse un usuario.
 */
function InvitesSection() {
  const { data: invites, isLoading } = useInvites();
  const issueInvite = useIssueInvite();
  const revokeInvite = useRevokeInvite();

  const [note, setNote] = useState('');
  const [issued, setIssued] = useState<IssuedInvite | null>(null);
  const [copied, setCopied] = useState(false);

  return (
    <section className="rounded-panel border border-line bg-surface">
      <header className="border-b border-line-soft bg-sunken px-4 py-[13px]">
        <h2 className="font-display text-sm font-semibold">Tokens de registro</h2>
        <p className="mt-0.5 text-[12.5px] text-subtle text-pretty">
          Manda uno a quien quieras sumar. Se registra con su cuenta de Google y elige su nombre,
          su puesto y su contrasena. Cada token vale 24 horas y una sola vez.
        </p>
      </header>

      {issued && (
        <div className="m-4 rounded-panel border border-brand-line bg-brand-tint p-4">
          <h3 className="font-display text-sm font-semibold">
            Token generado{issued.note ? ` para ${issued.note}` : ''}
          </h3>
          <p className="mt-2 break-all rounded-control bg-surface px-3 py-2 font-mono text-[13px] text-brand-deep">
            {issued.token}
          </p>
          <p className="mt-2 text-[11.5px] text-subtle text-pretty">
            Copialo ahora: no se guarda en claro y no podras volver a verlo. Caduca el{' '}
            {new Date(issued.expiresAt).toLocaleString('es')}.
          </p>
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={() => {
                void navigator.clipboard.writeText(issued.token).then(() => setCopied(true));
              }}
              className="rounded-control bg-brand px-3 py-1.5 text-[12.5px] font-semibold text-brand-on transition-colors hover:bg-brand-strong"
            >
              {copied ? 'Copiado' : 'Copiar token'}
            </button>
            <button
              type="button"
              onClick={() => {
                setIssued(null);
                setCopied(false);
              }}
              className="rounded-control border border-brand-line px-3 py-1.5 text-[12.5px] font-semibold transition-colors hover:bg-brand-tint-strong"
            >
              Ya lo guarde
            </button>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-end gap-3 px-4 py-4">
        <div className="min-w-[240px] flex-1">
          <label htmlFor="invite-note" className="mb-1.5 block text-[12.5px] font-semibold">
            Para quien <span className="font-normal text-faint">(opcional, solo recordatorio)</span>
          </label>
          <input
            id="invite-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Ana, del equipo de QA"
            className="w-full rounded-control border border-line-strong bg-surface px-3 py-2 text-[13.5px] outline-none focus:border-brand"
          />
        </div>
        <button
          type="button"
          disabled={issueInvite.isPending}
          onClick={() =>
            issueInvite.mutate(note.trim() || undefined, {
              onSuccess: (invite) => {
                setIssued(invite);
                setCopied(false);
                setNote('');
              },
            })
          }
          className="rounded-control bg-brand px-4 py-2 text-[13px] font-semibold text-brand-on transition-colors hover:bg-brand-strong disabled:opacity-50"
        >
          {issueInvite.isPending ? 'Generando…' : 'Generar token'}
        </button>
      </div>

      {issueInvite.error && (
        <div className="px-4 pb-3">
          <ErrorNotice>{(issueInvite.error as Error).message}</ErrorNotice>
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-6">
          <Spinner />
        </div>
      ) : invites && invites.length > 0 ? (
        <ul className="border-t border-line-soft">
          {invites.map((invite) => (
            <li
              key={invite.id}
              className="flex items-center gap-3 border-b border-line-soft px-4 py-2.5 text-[13px] last:border-b-0"
            >
              <InviteStatusBadge status={invite.status} />
              <span className="min-w-0 flex-1 truncate">
                {invite.note ?? <span className="text-faint">Sin nota</span>}
              </span>
              <span className="text-[12px] text-subtle">
                {invite.status === 'USED' && invite.usedBy
                  ? `La uso ${invite.usedBy.fullName}`
                  : `Caduca ${new Date(invite.expiresAt).toLocaleString('es')}`}
              </span>
              {invite.status === 'PENDING' && (
                <button
                  type="button"
                  onClick={() => revokeInvite.mutate(invite.id)}
                  className="rounded-[7px] border border-line-strong px-2 py-1 text-[11.5px] font-medium text-danger-text transition-colors hover:border-danger"
                >
                  Revocar
                </button>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <p className="border-t border-line-soft px-4 py-5 text-center text-[13px] text-faint">
          Todavia no has generado ningun token.
        </p>
      )}
    </section>
  );
}

function InviteStatusBadge({ status }: { status: InviteStatus }) {
  const meta = {
    PENDING: { label: 'PENDIENTE', className: 'border-info-line bg-info-soft text-info-text' },
    USED: { label: 'USADO', className: 'border-success-line bg-success-soft text-success-text' },
    EXPIRED: { label: 'CADUCADO', className: 'border-line-strong bg-muted-surface text-muted' },
  }[status];

  return (
    <span
      className={`flex-none rounded-badge border px-1.5 py-px text-[10px] font-bold tracking-[.06em] ${meta.className}`}
    >
      {meta.label}
    </span>
  );
}

function TemporaryPasswordNotice({
  data,
  onDismiss,
}: {
  data: CreatedUserResponse;
  onDismiss: () => void;
}) {
  const [copied, setCopied] = useState(false);

  return (
    <div
      role="alert"
      className="rounded-panel border border-brand-line bg-brand-tint p-4 text-[13px] text-ink"
    >
      <h2 className="font-display text-sm font-semibold">
        Credenciales de {data.user.fullName}
      </h2>
      <dl className="mt-3 flex flex-wrap gap-x-8 gap-y-1.5">
        <div className="flex items-baseline gap-2">
          <dt className="text-subtle">Correo</dt>
          <dd className="font-mono font-semibold">{data.user.email}</dd>
        </div>
        <div className="flex items-baseline gap-2">
          <dt className="text-subtle">Clave temporal</dt>
          <dd className="rounded-md bg-white px-2 py-0.5 font-mono font-semibold text-brand-strong">
            {data.temporaryPassword}
          </dd>
        </div>
      </dl>
      <p className="mt-2.5 text-[11.5px] text-subtle text-pretty">
        Copiala ahora: no se guarda en claro y no podras volver a verla.
      </p>
      <div className="mt-3 flex gap-2">
        <button
          type="button"
          onClick={() => {
            void navigator.clipboard
              .writeText(`Correo: ${data.user.email}\nClave temporal: ${data.temporaryPassword}`)
              .then(() => setCopied(true));
          }}
          className="rounded-control bg-brand px-3 py-1.5 text-[12.5px] font-semibold text-brand-on transition-colors hover:bg-brand-strong"
        >
          {copied ? 'Copiado' : 'Copiar'}
        </button>
        <button
          type="button"
          onClick={onDismiss}
          className="rounded-control border border-brand-line px-3 py-1.5 text-[12.5px] font-semibold transition-colors hover:bg-brand-tint-strong"
        >
          Ya la guarde
        </button>
      </div>
    </div>
  );
}

function Field({
  label,
  name,
  type = 'text',
  required,
  placeholder,
}: {
  label: string;
  name: string;
  type?: string;
  required?: boolean;
  placeholder?: string;
}) {
  return (
    <div>
      <label htmlFor={name} className="mb-1.5 block text-[12.5px] font-semibold">
        {label}
      </label>
      <input
        id={name}
        name={name}
        type={type}
        required={required}
        placeholder={placeholder}
        className="w-full rounded-control border border-line-strong bg-surface px-3 py-2 text-[13.5px] outline-none focus:border-brand"
      />
    </div>
  );
}
